import { TZDateMini } from '@date-fns/tz';
import clsx from 'clsx';
import { endOfDay, startOfDay } from 'date-fns';
import { noop } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import type { RequireAtLeastOne } from 'type-fest';

import { Button } from '@dynatrace/strato-components/buttons';
import { CalendarIcon } from '@dynatrace/strato-icons';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { SelectedOverCalendarContext } from './contexts/SelectedOverCalendarContext.js';
import { dateRangeSelectorCSS } from './DateRangeSelector.sty.js';
import { isEqualRangeSelection, removePrecision } from './helpers.js';
import type { DateRangeSelectorProps, DateRangeSelectorRef } from './types.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import type { TimeframeV2 } from '../../core/types/time.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { Overlay } from '../../overlays/overlay/Overlay.js';
import { globalSprinkles } from '../../styles/safe-sprinkles.js';
import { Calendar } from '../calendar/Calendar.js';
import { FormFieldMessageContext } from '../forms-core/contexts/FormFieldMessageContext.js';

/**
 * Calendar widget trigger and overlay component.
 */
export const DateRangeSelector = forwardRef<
  DateRangeSelectorRef,
  DateRangeSelectorProps
>((props, forwardedRef) => {
  const {
    value,
    onChange,
    disabled,
    readOnly,
    error,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    min,
    max,
    errorFrom,
    errorTo,
    'aria-disabled': ariaDisabledProp,
    precision,
    ...remainingProps
  } = props;
  const [open, setOpen] = useState(false);
  const [calendarValue, setCalendarValue] = useState(value);

  const intl = useIntl();
  const calendarTriggerLabel = intl.formatMessage({
    defaultMessage: 'DateRangePicker calendar trigger',
    id: 'K2M82PM/gcTo3fjq',
    description: 'Text shown for the datetimerangepicker calendar trigger.',
  });

  const { setCalendarOnChangeFlag, setSelectedOverCalendar } = useContext(
    SelectedOverCalendarContext,
  );

  // update calendar value state and call onChange if necessary
  const updateCalendarValue = useCallback(
    (newInternalValue: TimeframeV2 | null) => {
      if (readOnly) {
        return;
      }
      if (!newInternalValue) {
        onChange(null);
      } else if (newInternalValue.from && newInternalValue.to) {
        const timeZone = getTimezone();

        // only change selectedOverCalendar when it is a full day
        const to = new TZDateMini(newInternalValue.to.absoluteDate, timeZone);
        const from = new TZDateMini(
          newInternalValue.from.absoluteDate,
          timeZone,
        );

        const isToAtEndOfDay =
          removePrecision(endOfDay(to).toISOString(), precision) ===
          removePrecision(newInternalValue.to.absoluteDate, precision);
        const isToAtMax = max === to.toISOString();

        const isFromAtStartOfDay =
          removePrecision(startOfDay(from).toISOString(), precision) ===
          removePrecision(newInternalValue.from.absoluteDate, precision);
        const isFromAtMin = min === from.toISOString();

        if (
          (isToAtEndOfDay || isToAtMax) &&
          (isFromAtStartOfDay || isFromAtMin)
        ) {
          setCalendarOnChangeFlag?.(true);
          setSelectedOverCalendar?.(true);
        }

        setOpen(false);
        !isEqualRangeSelection(newInternalValue, value) &&
          onChange(newInternalValue);
      }
      setCalendarValue(newInternalValue);
    },
    [
      readOnly,
      onChange,
      precision,
      value,
      setCalendarOnChangeFlag,
      setSelectedOverCalendar,
      max,
      min,
    ],
  );

  // apply controlled value update to the calendar value if necessary
  useEffect(() => {
    setCalendarValue((prevValue) => {
      if (errorTo === 'invalid' && errorFrom === 'invalid') {
        return { from: null, to: null };
      }
      if (errorFrom === 'invalid') {
        return { from: null, to: value?.to || null };
      }
      if (errorTo === 'invalid') {
        return { from: value?.from || null, to: null };
      }
      return isEqualRangeSelection(prevValue, value) ? prevValue : value;
    });
  }, [errorFrom, errorTo, value]);

  const {
    targetRef,
    triggerRef,
    overlayRef,
    overlayState,
    overlayProps,
    overlayTriggerProps,
  } = useOverlayWithTrigger({
    placement: 'bottom-end',
    offset: 8,
    shouldCloseOnBlur: false,
    open,
    onOpenChange: setOpen,
    // the overlay should not open when disabled or readOnly
    disabled: disabled || readOnly,
  });

  const rootRef = useMergeRefs<HTMLButtonElement | null>([
    triggerRef,
    targetRef,
  ]);

  useImperativeHandle(
    forwardedRef,
    (): DateRangeSelectorRef => ({
      open: () => setOpen(true),
      close: () => setOpen(false),
    }),
    [],
  );

  const { focusClassName } = useFocusRing({
    focusWithin: false,
    isMinimal: false,
    disabled: disabled,
    color: error ? 'critical' : 'neutral',
  });
  const formFieldMessageContextValue = useMemo(
    () => ({
      dispatch: noop,
      state: {
        customMessages: [],
        formControlMessages: [],
        isDisabled: disabled,
        showError: error,
      },
    }),
    [error, disabled],
  );

  return (
    <>
      <Button
        ref={rootRef}
        data-testid={dataTestId}
        className={clsx(
          dateRangeSelectorCSS,
          focusClassName,
          consumerClassName,
        )}
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        readOnly={readOnly}
        style={consumerStyle}
        aria-label={calendarTriggerLabel}
        {...remainingProps}
        {...overlayTriggerProps}
        size="condensed"
        color={error ? 'critical' : 'neutral'}
        disabled={disabled}
        aria-disabled={ariaDisabledProp}
        variant="emphasized"
      >
        <Button.Prefix>
          <CalendarIcon />
        </Button.Prefix>
      </Button>
      <Overlay
        ref={overlayRef}
        isOpen={overlayState.isOpen}
        overlayProps={overlayProps}
        className={globalSprinkles({
          paddingLeft: 16,
          paddingTop: 16,
          paddingRight: 16,
          paddingBottom: 16,
        })}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        <FormFieldMessageContext.Provider value={formFieldMessageContextValue}>
          <Calendar
            value={
              calendarValue?.from || calendarValue?.to
                ? (calendarValue as RequireAtLeastOne<TimeframeV2>)
                : null
            }
            type="range"
            onChange={updateCalendarValue}
            required={false}
            min={min}
            max={max}
            aria-disabled={ariaDisabledProp}
            precision={precision}
          />
        </FormFieldMessageContext.Provider>
      </Overlay>
    </>
  );
});
